import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { ApiService, HomeAssistantResponse } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { StorageService } from 'src/app/services/storage.service';
import { ChatService } from 'src/app/services/chat.service';

export interface ChatMessage {
  id: string;
  author: 'assistant' | 'user';
  text: string;
  time: string;
  type?: 'text' | 'menu' | 'departments' | 'doctors' | 'patient_form' | 'dates' | 'slots' | 'confirmation';
  data?: any;
}

type BookingStep = 'idle' | 'department' | 'doctor' | 'patient' | 'date' | 'slot' | 'complete';

@Component({
  selector: 'app-ai-booking',
  templateUrl: './ai-booking.component.html',
  styleUrls: ['./ai-booking.component.scss']
})
export class AiBookingComponent implements OnInit {
  @ViewChild('chatScroll') private chatScrollContainer!: ElementRef;

  isOpen = true; // Open by default on this dedicated page
  isTyping = false;
  userInput = '';
  step: BookingStep = 'idle';

  messages: ChatMessage[] = [];
  departments: any[] = [];
  doctors: any[] = [];
  dates: string[] = [];
  slots: any[] = [];

  selectedDepartment: any = null;
  selectedDoctor: any = null;
  selectedDate = '';
  selectedSlot: any = null;
  doctorBranchId: any = null;

  loading = false;
  error = '';

  patientForm = this.fb.group({
    name: ['', [Validators.required, Validators.pattern('^[a-zA-Z ]{3,150}$')]],
    gender: ['', Validators.required],
    dob: ['', Validators.required],
    mrNumber: ['']
  });

  menuOptions = [
    { id: 'book', icon: 'calendar_month', title: 'Book an Appointment', subtitle: 'Find & schedule with a doctor' },
    { id: 'departments', icon: 'medical_services', title: 'Browse Specialities', subtitle: 'Explore clinical departments' },
    { id: 'doctors', icon: 'person_search', title: 'Find Doctors', subtitle: 'View all verified specialists' },
    { id: 'bookings', icon: 'receipt_long', title: 'Check My Bookings', subtitle: 'Track your scheduled visits' },
    { id: 'faq', icon: 'help_outline', title: 'Service Menu & FAQs', subtitle: 'Common questions & clinic info' }
  ];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private authService: AuthService,
    private storage: StorageService,
    private router: Router,
    public chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.chatService.open();
    this.initChat();
    this.loadDepartmentsData();
  }

  initChat(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages = [
      {
        id: 'msg-1',
        author: 'assistant',
        text: '👋 Hello! I am Mily, your MedEase Live Health Champion. How can I assist you today?',
        time: timeNow,
        type: 'text'
      },
      {
        id: 'msg-2',
        author: 'assistant',
        text: 'You can choose an option below or type naturally in the chat (e.g., “I need a skin doctor tomorrow”).',
        time: timeNow,
        type: 'menu'
      }
    ];
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  scrollToBottom(): void {
    try {
      if (this.chatScrollContainer) {
        this.chatScrollContainer.nativeElement.scrollTop = this.chatScrollContainer.nativeElement.scrollHeight;
      }
    } catch (_) {}
  }

  loadDepartmentsData(): void {
    this.apiService.getAllSpecialities().subscribe({
      next: (response: any) => {
        this.departments = response?.success ? response.data.data : [];
      },
      error: () => {}
    });
  }

  handleMenuClick(optionId: string): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    switch (optionId) {
      case 'book':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Book an Appointment',
          time: timeNow
        });
        this.startBookingFlow();
        break;

      case 'departments':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Browse Specialities',
          time: timeNow
        });
        this.showDepartments();
        break;

      case 'doctors':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Find Doctors',
          time: timeNow
        });
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Taking you to our doctors directory...',
          time: timeNow
        });
        setTimeout(() => this.router.navigate(['/doctors']), 800);
        break;

      case 'bookings':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Check My Bookings',
          time: timeNow
        });
        if (this.authService.getToken()) {
          this.router.navigate(['/booking-list']);
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'Please log in to view your bookings and appointment history.',
            time: timeNow
          });
          setTimeout(() => this.router.navigate(['/login']), 1200);
        }
        break;

      case 'faq':
        this.messages.push({
          id: `usr-${Date.now()}`,
          author: 'user',
          text: 'Service Menu & FAQs',
          time: timeNow
        });
        this.router.navigate(['/faq']);
        break;
    }
    setTimeout(() => this.scrollToBottom(), 100);
  }

  startBookingFlow(): void {
    this.step = 'department';
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.showTyping();
    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'Please select a medical department or specialty to see available specialists:',
        time: timeNow,
        type: 'departments',
        data: this.departments
      });
      this.scrollToBottom();
    }, 600);
  }

  showDepartments(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `ast-${Date.now()}`,
      author: 'assistant',
      text: 'Here are the medical departments available at MedEase:',
      time: timeNow,
      type: 'departments',
      data: this.departments
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  chooseDepartment(department: any): void {
    this.selectedDepartment = department;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: department.title,
      time: timeNow
    });

    this.step = 'doctor';
    this.showTyping();

    this.apiService.getBranchDoctorsList({ specialtyId: department.id }).subscribe({
      next: (response: any) => {
        this.isTyping = false;
        this.doctors = response?.success ? response.data.data : [];
        if (this.doctors && this.doctors.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `Found ${this.doctors.length} doctor(s) in ${department.title}. Select a doctor to continue:`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'doctors',
            data: this.doctors
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `There are currently no doctors scheduled for ${department.title}. Please choose another specialty or ask me for alternatives.`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'menu'
          });
        }
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Unable to retrieve doctors for this department at the moment.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      }
    });
  }

  chooseDoctor(doctor: any): void {
    this.selectedDoctor = doctor;
    this.doctorBranchId = doctor.branches?.[0]?.branch?.id;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');

    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `Dr. ${doctor.title}`,
      time: timeNow
    });

    this.step = 'patient';
    this.showTyping();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: `Great choice! Please provide the patient details for the appointment with Dr. ${doctor.title}:`,
        time: timeNow,
        type: 'patient_form'
      });
      this.scrollToBottom();
    }, 500);
  }

  submitPatientDetails(): void {
    if (this.patientForm.invalid) {
      this.patientForm.markAllAsTouched();
      return;
    }
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `Patient: ${this.patientForm.value.name}`,
      time: timeNow
    });

    this.step = 'date';
    this.showTyping();

    this.apiService.getMonthSlots(null, false, this.selectedDoctor.id, this.doctorBranchId)
      .then((response: any) => {
        this.isTyping = false;
        this.dates = response?.success ? (response.data.date || []) : [];
        if (this.dates && this.dates.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'Please select your preferred appointment date:',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'dates',
            data: this.dates
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'There are no open dates for this doctor this month. You may try another specialist.',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'menu'
          });
        }
        this.scrollToBottom();
      })
      .catch(() => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Unable to check appointment calendar right now.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      });
  }

  chooseDate(date: string): void {
    this.selectedDate = date;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: formatDate(date, 'EEEE, MMM d, y', 'en-US'),
      time: timeNow
    });

    this.step = 'slot';
    this.showTyping();

    this.apiService.getSlotsByDate(date, false, this.selectedDoctor.id, this.doctorBranchId, null, false).subscribe({
      next: (response: any) => {
        this.isTyping = false;
        const now = new Date();
        this.slots = response?.success
          ? response.data.filter(
              (slot: any) =>
                !slot.blocked &&
                !slot.is_break &&
                !slot.slotsLimited &&
                (!slot.time_limits ||
                  (now >= new Date(slot.time_limits.booking_start_time) &&
                    now <= new Date(slot.time_limits.booking_end_time)))
            )
          : [];

        if (this.slots && this.slots.length > 0) {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: `Available time slots on ${formatDate(date, 'MMM d', 'en-US')}:`,
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'slots',
            data: this.slots
          });
        } else {
          this.messages.push({
            id: `ast-${Date.now()}`,
            author: 'assistant',
            text: 'No available slots found on this date. Please pick a different date.',
            time: formatDate(new Date(), 'h:mm a', 'en-US'),
            type: 'dates',
            data: this.dates
          });
        }
        this.scrollToBottom();
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'Unable to load time slots right now.',
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });
        this.scrollToBottom();
      }
    });
  }

  chooseSlot(slot: any): void {
    this.selectedSlot = slot;
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    const timeFormatted = formatDate(slot.start_time, 'h:mm a', 'en-US');

    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: `${timeFormatted} slot`,
      time: timeNow
    });

    this.step = 'complete';
    this.showTyping();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        id: `ast-${Date.now()}`,
        author: 'assistant',
        text: 'Your appointment is ready to be confirmed! Review the summary below and click proceed to finalize your booking.',
        time: timeNow,
        type: 'confirmation',
        data: {
          doctor: this.selectedDoctor,
          date: this.selectedDate,
          slot: this.selectedSlot,
          patient: this.patientForm.value
        }
      });
      this.scrollToBottom();
    }, 500);
  }

  continueToBooking(): void {
    this.storage.set(
      'ai-booking-prefill',
      JSON.stringify({
        doctorId: this.selectedDoctor?.id,
        slotId: this.selectedSlot?.id,
        date: this.selectedDate,
        patient: this.patientForm.value
      })
    );
    if (!this.authService.getToken()) {
      this.router.navigate(['/login']);
      return;
    }
    this.router.navigate(['/booking', this.selectedDoctor.id]);
  }

  sendMessage(): void {
    const text = this.userInput.trim();
    if (!text || this.isTyping) {
      return;
    }

    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `usr-${Date.now()}`,
      author: 'user',
      text: text,
      time: timeNow
    });
    this.userInput = '';
    this.showTyping();
    this.scrollToBottom();

    // Natural Language processing via backend AI
    this.apiService.homeBookingAssistant(text).subscribe({
      next: (response: HomeAssistantResponse) => {
        this.isTyping = false;
        const reply = response?.data?.reply || response?.message || 'I understood your request.';
        const specialtyId = response?.data?.specialty_id;
        const specialty = response?.data?.specialty;

        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: reply,
          time: formatDate(new Date(), 'h:mm a', 'en-US')
        });

        if (specialtyId) {
          this.apiService.getBranchDoctorsList({ specialtyId }).subscribe({
            next: (docRes: any) => {
              const matchedDoctors = docRes?.success ? docRes.data.data : [];
              if (matchedDoctors.length > 0) {
                this.messages.push({
                  id: `ast-${Date.now()}`,
                  author: 'assistant',
                  text: `Here are available specialists for ${specialty || 'your condition'}:`,
                  time: formatDate(new Date(), 'h:mm a', 'en-US'),
                  type: 'doctors',
                  data: matchedDoctors
                });
              }
              this.scrollToBottom();
            },
            error: () => this.scrollToBottom()
          });
        } else {
          this.scrollToBottom();
        }
      },
      error: () => {
        this.isTyping = false;
        this.messages.push({
          id: `ast-${Date.now()}`,
          author: 'assistant',
          text: 'I am here to help. You can choose from our main service menu below or tell me which doctor you are looking for.',
          time: formatDate(new Date(), 'h:mm a', 'en-US'),
          type: 'menu'
        });
        this.scrollToBottom();
      }
    });
  }

  goToMainMenu(): void {
    const timeNow = formatDate(new Date(), 'h:mm a', 'en-US');
    this.messages.push({
      id: `ast-${Date.now()}`,
      author: 'assistant',
      text: 'Main Service Menu:',
      time: timeNow,
      type: 'menu'
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  restart(): void {
    this.step = 'idle';
    this.selectedDepartment = null;
    this.selectedDoctor = null;
    this.selectedSlot = null;
    this.selectedDate = '';
    this.doctors = [];
    this.dates = [];
    this.slots = [];
    this.error = '';
    this.patientForm.reset();
    this.initChat();
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private showTyping(): void {
    this.isTyping = true;
    setTimeout(() => this.scrollToBottom(), 50);
  }
}
